import React, { useState, useCallback, useEffect } from 'react';
import { statusLabel, statusStyle } from './reimbursementStatus';

const formatDate = (isoString) => {
    try {
        return new Date(isoString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        return isoString;
    }
};

// Paginated, most-recent-first list of the member's Reimbursements, styled to match
// PhonepeMyBookings.js's booking cards. `excludeIds` hides the ones already shown as cards above.
const ReimbursementHistory = ({ title, icon, apiFn, refreshKey, renderDetails, excludeIds = [] }) => {
    const [records, setRecords] = useState([]);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const loadPage = useCallback(async (nextPage) => {
        setIsLoading(true);
        setError('');
        try {
            const response = await apiFn(nextPage);
            const data = response.data?.data || {};
            const newRecords = Array.isArray(data.records) ? data.records : [];
            setRecords((prev) => (nextPage === 1 ? newRecords : [...prev, ...newRecords]));
            setHasMore(Boolean(data.hasMore));
            setPage(nextPage);
        } catch (err) {
            setError('Failed to load history. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [apiFn]);

    useEffect(() => {
        loadPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiFn, refreshKey]);

    return (
        <div className="mb-4">
            <div className="flex items-center gap-2 mb-3">
                {icon}
                <p className="text-xs font-bold text-gray-400 tracking-widest uppercase">{title}</p>
            </div>

            {isLoading && records.length === 0 && !error && (
                <div className="border border-gray-100 rounded-xl p-4 animate-pulse">
                    <div className="h-4 bg-gray-100 rounded w-1/2 mb-2" />
                    <div className="h-3 bg-gray-100 rounded w-3/4" />
                </div>
            )}

            {error && <p className="text-red-600 text-xs mb-2">{error}</p>}

            {!isLoading && records.filter((r) => !excludeIds.includes(r._id)).length === 0 && !error && (
                <p className="text-xs text-gray-400">No past reimbursements.</p>
            )}

            {records.filter((record) => !excludeIds.includes(record._id)).map((record) => (
                <div key={record._id} className="border border-gray-100 rounded-xl p-3 mb-2">
                    <div className="flex items-center justify-between mb-1">
                        <p className="text-xs text-gray-400">{formatDate(record.createdAt)}</p>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${statusStyle(record.reimbursement_status)}`}>
                            {statusLabel(record.reimbursement_status)}
                        </span>
                    </div>
                    <p className="text-sm text-gray-700">{renderDetails(record)}</p>
                </div>
            ))}

            {hasMore && (
                <button
                    onClick={() => loadPage(page + 1)}
                    disabled={isLoading}
                    className="w-full py-2 text-xs font-semibold text-purple-600 hover:text-purple-700 disabled:opacity-50"
                >
                    {isLoading ? 'Loading…' : 'Load more'}
                </button>
            )}
        </div>
    );
};

export default ReimbursementHistory;
